'use server';

/**
 * Where the "your story is ready" message will arrive.
 *
 * The product promises one message per order (`STORIES.CREATE.R15`), and the
 * waiting screen has to name the address or the promise is worthless
 * (`FIRST.R12`).
 *
 * It asks ACCOUNTS, because that is where a person's address and their Telegram
 * link live. The portal's own `family_accounts` rows are a leftover of the
 * pre-accounts world: for everyone who signed in after the cutover that table
 * is empty, so reading it said "we will tell you" to every new family — exactly
 * the screen a parent cannot trust. The legacy rows are still consulted, but
 * only as a fallback when accounts cannot be reached.
 */

import { SessionService } from '@/contexts/authentication';
import { logger } from '@/shared/infrastructure/logging';

const log = logger.child({ context: 'readyMessageChannel' });

export interface ReadyMessageChannel {
  channel: 'telegram' | 'email' | 'none';
  /** The address, when the channel is email — shown so the parent can check it. */
  email?: string;
}

export async function getReadyMessageChannel(): Promise<ReadyMessageChannel> {
  const session = await SessionService.getSession();
  const userId = session?.user?.id ?? null;
  if (!userId) return { channel: 'none' };
  const { sessionIdentityId } = await import('@/contexts/authentication/infrastructure/identityService');
  const identityId = sessionIdentityId(session?.user);

  const { isRealEmail } = await import('@/contexts/family/accounts/lib/realEmail');

  // ── what accounts knows: the verified address and the bot's own status ──
  if (identityId) {
    /**
     * `/identity/{id}/channels` is the canonical answer to "how can we reach
     * this person" — the same route the notification worker sends off, and the
     * same one ContentPlanNotifier falls back to. Asking it here is what keeps
     * the screen's promise and the message that follows it in agreement.
     */
    try {
      const { getAccountsUrl, accountsServiceToken } = await import(
        '@/contexts/authentication/infrastructure/identityService'
      );
      const res = await fetch(
        `${getAccountsUrl()}/identity/${encodeURIComponent(identityId)}/channels`,
        {
          headers: {
            Authorization: `Bearer ${accountsServiceToken('portal')}`,
            Accept: 'application/json',
          },
          cache: 'no-store',
          signal: AbortSignal.timeout(5000),
        },
      );
      if (res.ok) {
        const ch = (await res.json()) as {
          preferred?: 'telegram' | 'email' | null;
          emailAddress?: string | null;
        };
        if (ch.preferred === 'telegram') return { channel: 'telegram' };
        if (ch.preferred === 'email' && isRealEmail(ch.emailAddress)) {
          return { channel: 'email', email: ch.emailAddress as string };
        }
      }
    } catch (e) {
      log.warn('identity channels unavailable — trying the profile', {
        error: e instanceof Error ? e.message : String(e),
      });
    }

    try {
      const { getIdentityClient } = await import('@/contexts/authentication/infrastructure/identityService');
      const profile = await getIdentityClient('portal').getProfile(identityId);
      // 'enabled' means the person pressed start in the bot; 'unknown' means
      // nobody asked yet, which is not an address we may promise to use.
      if (profile?.notify === 'enabled') return { channel: 'telegram' };
      if (profile?.email && isRealEmail(profile.email)) {
        return { channel: 'email', email: profile.email };
      }
    } catch (e) {
      log.warn('identity profile unavailable — falling back to the legacy rows', {
        error: e instanceof Error ? e.message : String(e),
      });
    }
  } else {
    log.warn('session carries no identityId — the address can only come from the session itself');
  }

  // ── the address this session signed in with ──
  // A Google or magic-link sign-in puts a real, verified address in the
  // session. Reading it here is what stops the screen saying "we will tell
  // you" to somebody who just typed that address in: before this, a profile
  // lookup that failed (or a session minted without an identityId) fell
  // straight through to the pre-accounts rows, which are empty for everyone
  // who signed in after the cutover.
  const sessionEmail = session?.user?.email ?? null;
  if (isRealEmail(sessionEmail)) return { channel: 'email', email: sessionEmail };

  // ── the pre-accounts rows, for families that still have them ──
  try {
    const [{ getFamilyAccountRepository }, { pickTelegramAccount }] = await Promise.all([
      import('@/contexts/family/server'),
      import('@/contexts/notifications/utils/resolveTelegramChatId'),
    ]);
    const accounts = await getFamilyAccountRepository().getByUserId(userId);
    if (pickTelegramAccount(accounts)) return { channel: 'telegram' };
    const email = accounts.map((a) => a.email).find((e) => isRealEmail(e));
    if (email) return { channel: 'email', email };
  } catch {
    // The waiting screen must never fail on a courtesy.
  }

  return { channel: 'none' };
}
